import { z } from 'zod'

export const getReservationCalendarsQuerySchema = z.object({
  reservableCategory: z.string().optional(),
  weekStartDate: z.string().date().optional(),
})

export const getReservationCalendarParamsSchema = z.object({
  contractUuid: z.string().uuid(),
  calendarId: z.string(),
})

export const getReservationCalendarTimeSlotsParamsSchema = z.object({
  contractUuid: z.string().uuid(),
  calendarId: z.string().transform((val) => parseInt(val, 10)),
})

export const getReservationCalendarTimeSlotsQuerySchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
})

export const reservationCalendarSchema = z.object({
  id: z.number(),
  realPropertyHierarchyId: z.number(),
  name: z.string(),
  reservableCategory: z.string().nullable(),
  equipmentCategory: z.string().nullable(),
  areaType: z.string(),
  residentDetailsFi: z.string().nullable(),
  residentDetailsSv: z.string().nullable(),
  residentDetailsEn: z.string().nullable(),
  reservableTimeStart: z.string(),
  reservableTimeEnd: z.string(),
  weeklySlotDuration: z.string(),
  privateSlotDurationMon: z.string(),
  privateSlotDurationTue: z.string(),
  privateSlotDurationWed: z.string(),
  privateSlotDurationThu: z.string(),
  privateSlotDurationFri: z.string(),
  privateSlotDurationSat: z.string(),
  privateSlotDurationSun: z.string(),
  slotsPerPrivateReservationMon: z.number(),
  slotsPerPrivateReservationTue: z.number(),
  slotsPerPrivateReservationWed: z.number(),
  slotsPerPrivateReservationThu: z.number(),
  slotsPerPrivateReservationFri: z.number(),
  slotsPerPrivateReservationSat: z.number(),
  slotsPerPrivateReservationSun: z.number(),
  weeklySlotsPerDay: z.number(),
  reservableWeekdays: z.array(z.number()),
  maxReservedSlotsPerWeek: z.number().nullable(),
  complimentarySlotsPerWeek: z.number(),
  usedComplimentarySlotsThisWeek: z.number(),
  maxReservationsPerSlot: z.number(),
  allowSingleReservations: z.boolean(),
  allowRecurringReservations: z.boolean(),
  validFrom: z.string(),
  validUntil: z.string().nullable(),
  currentReservationCount: z.number(),
})

export const reservationCalendarsResponseSchema = z.array(
  reservationCalendarSchema
)

export const timeSlotReservationSchema = z.object({
  id: z.number(),
  reservationRecurrenceId: z.number().nullable(),
  additionalDetails: z.string().nullable(),
})

export const timeSlotSchema = z.object({
  id: z.number(),
  startsAt: z.string(),
  endsAt: z.string(),
  duration: z.string(),
  status: z.enum(['free', 'own', 'own_recurring', 'reserved']),
  isPrivateSlotStart: z.boolean(),
  reservations: z.array(timeSlotReservationSchema),
})

export const calendarMetadataSchema = z.object({
  id: z.number(),
  name: z.string(),
  reservableTimeStart: z.string(),
  reservableTimeEnd: z.string(),
  weeklySlotDuration: z.string(),
  privateSlotDurationMon: z.string(),
  privateSlotDurationTue: z.string(),
  privateSlotDurationWed: z.string(),
  privateSlotDurationThu: z.string(),
  privateSlotDurationFri: z.string(),
  privateSlotDurationSat: z.string(),
  privateSlotDurationSun: z.string(),
  slotsPerPrivateReservationMon: z.number(),
  slotsPerPrivateReservationTue: z.number(),
  slotsPerPrivateReservationWed: z.number(),
  slotsPerPrivateReservationThu: z.number(),
  slotsPerPrivateReservationFri: z.number(),
  slotsPerPrivateReservationSat: z.number(),
  slotsPerPrivateReservationSun: z.number(),
  weeklySlotsPerDay: z.number(),
  reservableWeekdays: z.array(z.number()),
  maxReservationsPerSlot: z.number(),
})

export const reservationCalendarTimeSlotsResponseSchema = z.object({
  timeSlots: z.array(timeSlotSchema),
  calendarMetadata: calendarMetadataSchema,
  pricesByDate: z.record(
    z.object({
      monthlyPrice: z.string().nullable(),
      upcomingRecurringUnitPrice: z.string().nullable(),
      upcomingRecurringUnitPriceValidFrom: z.string().nullable(),
      oncePrice: z.string().nullable(),
    })
  ),
})

export type GetReservationCalendarsQuery = z.infer<
  typeof getReservationCalendarsQuerySchema
>
export type GetReservationCalendarParams = z.infer<
  typeof getReservationCalendarParamsSchema
>
export type GetReservationCalendarTimeSlotsParams = z.infer<
  typeof getReservationCalendarTimeSlotsParamsSchema
>
export type GetReservationCalendarTimeSlotsQuery = z.infer<
  typeof getReservationCalendarTimeSlotsQuerySchema
>
export type ReservationCalendar = z.infer<typeof reservationCalendarSchema>
export const reservationRulesAndTermsParamsSchema = z.object({
  contractUuid: z.string().uuid(),
})

// Categories that are reservable through the slot/calendar reservation flow.
export const reservableCategorySchema = z.enum([
  'washing_machine',
  'allergy_washing_machine',
  'dryer',
  'mangle',
  'laundry_room',
  'house_sauna',
  'gym',
  'reservable_common_area',
])

export const reservationRulesAndTermsQuerySchema = z.object({
  // Rules & terms also cover extra services (storage, parking_spot), which are
  // leased as contract parts rather than booked via a reservation calendar, so
  // this extends the calendar-reservable categories with those two.
  reservableCategory: z.enum([
    ...reservableCategorySchema.options,
    'storage',
    'parking_spot',
  ]),
  language: z.enum(['fi', 'sv', 'en']),
  realPropertyHierarchyId: z
    .string()
    .optional()
    .transform((value) =>
      value === '' || value === undefined ? undefined : value
    )
    .pipe(z.coerce.number().int().optional()),
})

export const reservationRulesAndTermsResponseSchema = z.object({
  files: z.array(
    z.object({
      url: z.string().url(),
      displayFileName: z.string(),
      attachmentType: z.string().nullable(),
    })
  ),
  externalLinks: z.array(
    z.object({
      url: z.string().url(),
      title: z.string(),
      attachmentType: z.string().nullable(),
    })
  ),
})

export type ReservationRulesAndTermsResponse = z.infer<
  typeof reservationRulesAndTermsResponseSchema
>

export type ReservationCalendarsResponse = z.infer<
  typeof reservationCalendarsResponseSchema
>

export type TimeSlotForResident = z.infer<typeof timeSlotSchema>
export type ReservationCalendarTimeSlotsResponse = z.infer<
  typeof reservationCalendarTimeSlotsResponseSchema
>
